import { lazy, Suspense, type ComponentType, type ReactNode } from "react";
import { createBrowserRouter, Navigate, Outlet } from "react-router-dom";
import { AppShell } from "@/layout/AppShell";
// Eager, foundational-only: the login page is the destination of every failed
// session check, so lazy-loading it would add a chunk round-trip to the most
// common cold-start path.
import { LoginPage } from "@/features/auth/LoginPage";
import { useCurrentUser } from "@/features/auth/auth.hooks";
import { useAuthStore } from "@/features/auth/auth.store";
import { isAgencyAdmin } from "@/lib/permissions";
import { AppStartupSkeleton } from "@/ui/feedback/skeletons";
import { RouteErrorBoundary } from "./RouteErrorBoundary";
import { RequirePerm } from "./RoutePermissionGuard";
import { getRouteRule } from "./route-permissions";

/**
 * Route-level code splitting.
 *
 * Every business page is loaded only when its route is visited. Before this,
 * `router.tsx` statically imported ~50 pages, so the initial bundle contained
 * the entire application (including recharts and xlsx) before the first paint.
 *
 * All pages use NAMED exports, so the dynamic import is mapped to `default`.
 * The Suspense boundary lives inside `AppShell` (around `<Outlet/>`), which
 * means the sidebar and topbar stay mounted and interactive while a page chunk
 * downloads — only the content area shows a skeleton.
 */
function lazyPage<M extends Record<string, unknown>>(
  load: () => Promise<M>,
  pick: (module: M) => ComponentType,
) {
  return lazy(async () => ({ default: pick(await load()) }));
}

// ── Operations ────────────────────────────────────────────────────────────────
const DashboardPage = lazyPage(() => import("@/features/dashboard/DashboardPage"), (m) => m.DashboardPage);
const LeadsPage = lazyPage(() => import("@/features/leads/LeadsPage"), (m) => m.LeadsPage);
const ClientsPage = lazyPage(() => import("@/features/clients/ClientsPage"), (m) => m.ClientsPage);
const ClientDetailsPage = lazyPage(() => import("@/features/clients/ClientDetailsPage"), (m) => m.ClientDetailsPage);
const ServicesPage = lazyPage(() => import("@/features/services/ServicesPage"), (m) => m.ServicesPage);
const ProjectsPage = lazyPage(() => import("@/features/projects/ProjectsPage"), (m) => m.ProjectsPage);
const TasksPage = lazyPage(() => import("@/features/tasks/TasksPage"), (m) => m.TasksPage);
const TaskDetailsPage = lazyPage(() => import("@/features/tasks/TaskDetailsPage"), (m) => m.TaskDetailsPage);

// ── Marketing ─────────────────────────────────────────────────────────────────
const CampaignsPage = lazyPage(() => import("@/features/campaigns/CampaignsPage"), (m) => m.CampaignsPage);
const MarketingDashboardPage = lazyPage(() => import("@/features/marketing/MarketingDashboardPage"), (m) => m.MarketingDashboardPage);
const MarketingCampaignsPage = lazyPage(() => import("@/features/marketing/CampaignsPage"), (m) => m.CampaignsPage);
const ContentPage = lazyPage(() => import("@/features/marketing/ContentPage"), (m) => m.ContentPage);
const MarketingCalendarPage = lazyPage(() => import("@/features/marketing/MarketingCalendarPage"), (m) => m.MarketingCalendarPage);
const MarketingLibraryPage = lazyPage(() => import("@/features/marketing/MarketingLibraryPage"), (m) => m.MarketingLibraryPage);
const ApprovalsPage = lazyPage(() => import("@/features/marketing/ApprovalsPage"), (m) => m.ApprovalsPage);
const AdWalletsPage = lazyPage(() => import("@/features/marketing/AdWalletsPage"), (m) => m.AdWalletsPage);
const MarketingReportsPage = lazyPage(() => import("@/features/marketing/MarketingReportsPage"), (m) => m.MarketingReportsPage);
const PartenairesPage = lazyPage(() => import("@/features/marketing/PartenairesPage"), (m) => m.PartenairesPage);
const AffiliesPage = lazyPage(() => import("@/features/marketing/AffiliesPage"), (m) => m.AffiliesPage);

// ── Finance ───────────────────────────────────────────────────────────────────
const TreasuryPage = lazyPage(() => import("@/features/treasury/TreasuryPage"), (m) => m.TreasuryPage);
const ComptaOverviewPage = lazyPage(() => import("@/features/accounting/ComptaOverviewPage"), (m) => m.ComptaOverviewPage);
const TransactionsPage = lazyPage(() => import("@/features/accounting/TransactionsPage"), (m) => m.TransactionsPage);
const DebtsPage = lazyPage(() => import("@/features/accounting/DebtsPage"), (m) => m.DebtsPage);
const RecurringPage = lazyPage(() => import("@/features/accounting/RecurringPage"), (m) => m.RecurringPage);
const ComptaReportsPage = lazyPage(() => import("@/features/accounting/ComptaReportsPage"), (m) => m.ComptaReportsPage);
const InvoicesPage = lazyPage(() => import("@/features/invoices/InvoicesPage"), (m) => m.InvoicesPage);
const ExpensesPage = lazyPage(() => import("@/features/expenses/ExpensesPage"), (m) => m.ExpensesPage);
const SubscriptionsPage = lazyPage(() => import("@/features/subscriptions/SubscriptionsPage"), (m) => m.SubscriptionsPage);

// ── Reports & AI ──────────────────────────────────────────────────────────────
const StatisticsPage = lazyPage(() => import("@/features/statistics/StatisticsPage"), (m) => m.StatisticsPage);
const ReportsPage = lazyPage(() => import("@/features/reports/ReportsPage"), (m) => m.ReportsPage);
const ReportsHubPage = lazyPage(() => import("@/features/reports-hub/ReportsPage"), (m) => m.ReportsPage);
const AiDashboardPage = lazyPage(() => import("@/features/ai-agents/AiDashboardPage"), (m) => m.AiDashboardPage);
const AiChatPage = lazyPage(() => import("@/features/ai-agents/AiChatPage"), (m) => m.AiChatPage);

// ── Team & admin ──────────────────────────────────────────────────────────────
const TeamPage = lazyPage(() => import("@/features/team/TeamPage"), (m) => m.TeamPage);
const TeamDashboardPage = lazyPage(() => import("@/features/team/TeamDashboardPage"), (m) => m.TeamDashboardPage);
const DepartmentsPage = lazyPage(() => import("@/features/team/DepartmentsPage"), (m) => m.DepartmentsPage);
const PlanningPage = lazyPage(() => import("@/features/team/PlanningPage"), (m) => m.PlanningPage);
const PerformancePage = lazyPage(() => import("@/features/team/PerformancePage"), (m) => m.PerformancePage);
const RequestsPage = lazyPage(() => import("@/features/team/RequestsPage"), (m) => m.RequestsPage);
const TeamProfilePage = lazyPage(() => import("@/features/team/TeamProfilePage"), (m) => m.TeamProfilePage);
const AuditLogsPage = lazyPage(() => import("@/features/audit-logs/AuditLogsPage"), (m) => m.AuditLogsPage);
const SettingsPage = lazyPage(() => import("@/features/settings/SettingsPage"), (m) => m.SettingsPage);
// Page personnelle (spec §6) — aucune permission : c'est SON profil.
const ProfilePage = lazyPage(() => import("@/features/profile/ProfilePage"), (m) => m.ProfilePage);
const ArchivesPage = lazyPage(() => import("@/features/archives/ArchivesPage"), (m) => m.ArchivesPage);

// ── Public surfaces (outside the agency auth shell) ───────────────────────────
const PortalLoginPage = lazyPage(() => import("@/features/portal/PortalLoginPage"), (m) => m.PortalLoginPage);
const PortalApp = lazyPage(() => import("@/features/portal/PortalApp"), (m) => m.PortalApp);
const AffiliateLoginPage = lazyPage(() => import("@/features/affiliate-portal/AffiliateLoginPage"), (m) => m.AffiliateLoginPage);
const AffiliateApp = lazyPage(() => import("@/features/affiliate-portal/AffiliateApp"), (m) => m.AffiliateApp);
const SharedCampaignPage = lazyPage(() => import("@/features/campaigns/SharedCampaignPage"), (m) => m.SharedCampaignPage);

/** Standalone (non-shell) lazy route: needs its own Suspense boundary. */
function publicRoute(element: ReactNode) {
  return <Suspense fallback={<AppStartupSkeleton />}>{element}</Suspense>;
}

/**
 * Garde de permission par URL (registre RBAC §5, trous n°3/4/5) : applique à
 * chaque route la même règle que le menu (NAV_ITEMS), ou la règle explicite
 * de route-permissions.ts pour les sous-pages et les pages exclues (§8).
 * Retirer une page du menu ne suffit pas — l'URL doit être gardée aussi.
 */
function guarded(path: string, element: ReactNode) {
  return { path, element: <RequirePerm {...getRouteRule(`/${path}`)}>{element}</RequirePerm> };
}

/**
 * Session guard: the app shell requires a real session. Anonymous visitors are
 * sent to /login.
 *
 * While the session is still being checked we render the app skeleton instead of
 * redirecting: redirecting here is what made the login page flash on every
 * reload of an authenticated session. Nothing protected mounts in this state
 * (AppShell is a child of this route), and every data hook stays disabled.
 */
function RequireSession() {
  const status = useAuthStore((s) => s.status);
  if (status === "checking") return <AppStartupSkeleton />;
  if (status === "anonymous") return <Navigate to="/login" replace />;
  return <Outlet />;
}

/** Redirects to /dashboard for admins, /tasks for members. */
function RoleAwareIndex() {
  const { data: currentUser } = useCurrentUser();
  if (currentUser && isAgencyAdmin(currentUser)) return <Navigate to="/dashboard" replace />;
  return <Navigate to="/tasks" replace />;
}

/** Blocks access to Dashboard for non-admin users. */
function DashboardGuard() {
  const { data: currentUser } = useCurrentUser();
  if (currentUser && !isAgencyAdmin(currentUser)) return <Navigate to="/tasks" replace />;
  return <Outlet />;
}

export const router = createBrowserRouter([
  { path: "/login", element: <LoginPage />, errorElement: <RouteErrorBoundary /> },
  // Client portal — a fully separate auth surface (portal-scoped JWT), outside
  // the agency RequireSession/AppShell tree.
  { path: "/portal/login", element: publicRoute(<PortalLoginPage />), errorElement: <RouteErrorBoundary /> },
  { path: "/portal", element: publicRoute(<PortalApp />), errorElement: <RouteErrorBoundary /> },
  // Portail affilié — même modèle : surface d'auth séparée (JWT scope
  // "affiliate"), hors de l'arbre RequireSession/AppShell.
  { path: "/affilie/login", element: publicRoute(<AffiliateLoginPage />), errorElement: <RouteErrorBoundary /> },
  { path: "/affilie", element: publicRoute(<AffiliateApp />), errorElement: <RouteErrorBoundary /> },
  // Public read-only campaign report (share token — no auth)
  {
    path: "/shared/campagne/:token",
    element: publicRoute(<SharedCampaignPage />),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/",
    element: <RequireSession />,
    // Catches render errors AND failed lazy-chunk imports (stale deploy, offline)
    // so a broken import can never leave the app permanently blank.
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        element: <AppShell />,
        errorElement: <RouteErrorBoundary />,
        children: [
          { index: true, element: <RoleAwareIndex /> },
          {
            element: <DashboardGuard />,
            children: [guarded("dashboard", <DashboardPage />)],
          },
          guarded("leads", <LeadsPage />),
          guarded("clients", <ClientsPage />),
          // hérite de la page liste /clients (clients.view)
          guarded("clients/:clientId", <ClientDetailsPage />),
          guarded("services", <ServicesPage />),
          guarded("projects", <ProjectsPage />),
          // page exclue de la matrice v2 (§8) — blocage pur sauf OWNER/ADMIN
          guarded("campaigns", <CampaignsPage />),
          guarded("marketing", <MarketingDashboardPage />),
          guarded("marketing/campagnes", <MarketingCampaignsPage />),
          guarded("marketing/contenu", <ContentPage />),
          // page exclue de la matrice v2 (§8) — blocage pur sauf OWNER/ADMIN
          guarded("marketing/calendrier", <MarketingCalendarPage />),
          guarded("marketing/bibliotheque", <MarketingLibraryPage />),
          guarded("marketing/approbations", <ApprovalsPage />),
          // page exclue de la matrice v2 (§8, trou n°5) — blocage pur sauf OWNER/ADMIN
          guarded("marketing/budget", <AdWalletsPage />),
          // page exclue de la matrice v2 (§8) — blocage pur sauf OWNER/ADMIN
          guarded("marketing/rapports", <MarketingReportsPage />),
          guarded("marketing/partenaires", <PartenairesPage />),
          guarded("marketing/affilies", <AffiliesPage />),
          guarded("tasks", <TasksPage />),
          // hérite de la page liste /tasks (tasks.view)
          guarded("tasks/:taskId", <TaskDetailsPage />),
          guarded("treasury", <TreasuryPage />),
          guarded("compta", <ComptaOverviewPage />),
          // page exclue de la matrice v2 (§8) — blocage pur sauf OWNER/ADMIN
          guarded("compta/operations", <TransactionsPage />),
          guarded("compta/dettes", <DebtsPage />),
          guarded("compta/recurrentes", <RecurringPage />),
          // page exclue de la matrice v2 (§8) — blocage pur sauf OWNER/ADMIN
          guarded("compta/rapports", <ComptaReportsPage />),
          // page exclue de la matrice v2 (§8, trou n°4 : 100 % financière) — blocage pur sauf OWNER/ADMIN
          guarded("statistics", <StatisticsPage />),
          guarded("reports", <ReportsPage />),
          // Spec « dernières modifications » §7 — page Rapports UNIQUE
          // (les anciennes /rapports/generer et /rapports/enregistres sont retirées).
          guarded("rapports", <ReportsHubPage />),
          guarded("agents-ia", <AiDashboardPage />),
          guarded("agents-ia/chat", <AiChatPage />),
          guarded("invoices", <InvoicesPage />),
          // page exclue de la matrice v2 (§8) — blocage pur sauf OWNER/ADMIN
          guarded("expenses", <ExpensesPage />),
          // page exclue de la matrice v2 (§8) — blocage pur sauf OWNER/ADMIN
          guarded("subscriptions", <SubscriptionsPage />),
          guarded("team", <TeamPage />),
          guarded("team/dashboard", <TeamDashboardPage />),
          guarded("team/organisation", <DepartmentsPage />),
          // pages exclues de la matrice v2 (§8) — blocage pur sauf OWNER/ADMIN
          guarded("team/planning", <PlanningPage />),
          guarded("team/performance", <PerformancePage />),
          guarded("team/demandes", <RequestsPage />),
          // Devrait hériter de /team (team.view) SAUF auto-consultation : le store
          // auth n'expose pas l'id de membre (seulement le JWT `sub`), donc on
          // laisse passer et le backend décide (§5 trou n°2, Phase 2).
          guarded("team/:memberId", <TeamProfilePage />),
          // Journal des opérations (spec §4) — lecture seule, audit-logs.view
          guarded("journal", <AuditLogsPage />),
          guarded("settings", <SettingsPage />),
          // Profil personnel — règle vide dans route-permissions.ts (accessible
          // à tout utilisateur connecté, l'API /me/* agit sur lui-même).
          guarded("profile", <ProfilePage />),
          // page exclue de la matrice v2 (§8) — blocage pur sauf OWNER/ADMIN
          guarded("archives", <ArchivesPage />),
          { path: "*", element: <RoleAwareIndex /> },
        ],
      },
    ],
  },
]);
